import { RouteComponentProps, Router } from "@reach/router"
import { StatusBarPlacement } from "@shared/primitive-components"
import { useLDFlags } from "@shared/integration/launchdarkly"
import { useEffect } from "react"
import { FleetStats } from "../../api/fleet"
import { PATHS } from "../../const/paths"
import { usePolicyAndFleet, useUI } from "../../contexts"
import { usePolicyAccessCapabilities } from "../../hooks"
import { isConnectivityTargetMet } from "../../utils"
import { LowConnectivityBanner } from "../components"
import { NoVehiclesConnected, TargetNotMet } from "../components/SafetyInsightsComponents"
import { SafetyOverview, Speeding, Vehicles, Trips as TripsOld } from "./SafetyInsightsPages"
import { Vehicle, Trip as TripOld } from "./SafetyInsightsPages/individualDetailPages"
import { Trips as TripsNew, Trip as TripNew, TripsVrn as TripsVrnNew } from "../../integration"

export const SAFETY_INSIGHTS_ROOT_DTI = `safety_insights_root`
export const NO_CONNECTED_VEHICLES_BAR_DTI = `safety_insights_no_connected_vehicles_bar`
export const CONNECTIVITY_THRESHOLD_BAR_DTI = `safety_insights_connectivity_threshold_bar`
export const CONNECTIVITY_TARGET_BAR_DTI = `safety_insights_connectivity_target_bar`

export const DISMISS_CONNECTIVITY_BAR = `connectivity_banner_dismiss`

export const SafetyInsightsRouter: React.FC<RouteComponentProps> = () => {
  const { connectivity, policy } = usePolicyAndFleet()
  const { isRebatePolicy, isRebateV2Policy } = usePolicyAccessCapabilities()
  const { setStatusBar, dismissStatusBar } = useUI()
  const ldFlags = useLDFlags()
  const { useNewTripViews } = ldFlags

  const handleConnectivityStatus = (stats: FleetStats | null) => {
    if (!stats || !policy.connectivityRatio) return

    const ratio = policy.connectivityRatio ?? 0
    const connectedPercentage = Math.round(ratio * 100)
    const disconnectedPercentage = 100 - connectedPercentage

    const statusBar = {
      placement: `top` as StatusBarPlacement,
      dismissDataTestId: `connectivity_banner_dismiss`,
    }

    if (connectedPercentage === 0) {
      setStatusBar({
        type: `error`,
        message: NoVehiclesConnected(isRebatePolicy),
        dataTestId: NO_CONNECTED_VEHICLES_BAR_DTI,
        onDismiss: dismissStatusBar,
        ...statusBar,
      })
      return
    }
    if (!isConnectivityTargetMet(connectedPercentage, disconnectedPercentage) && !isRebateV2Policy) {
      setStatusBar({
        type: isRebateV2Policy ? `error` : `info`,
        message: TargetNotMet(isRebatePolicy, isRebateV2Policy),
        dataTestId: CONNECTIVITY_TARGET_BAR_DTI,
        onDismiss: isRebateV2Policy ? undefined : dismissStatusBar,
        ...statusBar,
      })
    }
  }

  useEffect(() => {
    handleConnectivityStatus(connectivity.data ?? null)
    return () => {
      setStatusBar(undefined)
    }
  }, [connectivity.data])

  return (
    <div data-testid={SAFETY_INSIGHTS_ROOT_DTI}>
      {!!(connectivity.data || policy.connectivityRatio) && <LowConnectivityBanner policy={policy} />}
      <Router>
        <SafetyOverview path="/" default />
        <Speeding path={`/${PATHS.INSIGHTS.SPEEDING}`} />
        <Vehicles path={`/${PATHS.INSIGHTS.VEHICLES}`} />
        <Vehicle path={`/${PATHS.INSIGHTS.VEHICLES}/:vrn`} />
        {useNewTripViews ? (
          <>
            <TripsVrnNew path={`/${PATHS.INSIGHTS.VEHICLES}/:vrn/trips`} />
            <TripsNew path={`/${PATHS.INSIGHTS.TRIPS}`} />
            <TripNew path={`/${PATHS.INSIGHTS.TRIPS}/:tripId`} />
          </>
        ) : (
          <>
            <TripsOld path={`/${PATHS.INSIGHTS.TRIPS}`} />
            <TripOld path={`/${PATHS.INSIGHTS.TRIPS}/:tripId`} />
          </>
        )}
      </Router>
    </div>
  )
}
